// Global imports
import { getProductPageLink } from './product'

// Types
import type {
  ProductDocumentRow,
  ProductDocumentSearchRow,
  ProductDocumentType,
  ProductSearchItem,
  SanityProductDocument,
} from '~/types'

export const PRODUCT_DOCUMENTS_PATH = '/product-documents'

export const PRODUCT_DOCUMENTS_PAGE_SIZE = 10

// The most words a search is split into
const MAX_SEARCH_WORDS = 6

const DOCUMENT_TYPES: Record<
  ProductDocumentType,
  { label: string; name: string }
> = {
  dop: { label: 'DoP', name: 'Declaration of Performance' },
  dataSheet: { label: 'Data sheet', name: 'Data sheet' },
  productGuide: { label: 'Product guide', name: 'Product guide' },
}

// Short label for the type chip, e.g. "DoP"
export const getDocumentTypeLabel = (type: ProductDocumentType): string =>
  DOCUMENT_TYPES[type]?.label ?? 'Document'

/*
  Sanity's ?dl= parameter makes the CDN serve the file as a download with that name.
  The CDN echoes the name into the header as-is, so anything that would need
  URL-encoding (spaces, brackets) becomes a hyphen or browsers save "%20" literally.
*/
const getDocumentDownloadUrl = ({
  url,
  originalFilename,
}: NonNullable<SanityProductDocument['file']>): string => {
  const filename = originalFilename
    ?.replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/-(\.|$)/g, '$1')

  return filename ? `${url}?dl=${filename}` : url
}

// Only http(s) web links are used; anything else (e.g. javascript:) is dropped even if it reaches the CMS
const getWebLink = (url?: string): string | null => {
  if (!url) return null

  try {
    const { protocol } = new URL(url)

    return protocol === 'https:' || protocol === 'http:' ? url : null
  } catch {
    return null
  }
}

// Where a row's button goes: our hosted PDF as a download, or the supplier's web page
const getDocumentHref = (document: SanityProductDocument): string | null => {
  if (document.source === 'link') return getWebLink(document.url)

  return document.file ? getDocumentDownloadUrl(document.file) : null
}

// Turns each entry into one row per product it covers, sorted by product name
export const flattenProductDocuments = (
  documents: SanityProductDocument[]
): ProductDocumentSearchRow[] =>
  documents
    .flatMap(document => {
      const href = getDocumentHref(document)

      if (!href) return []

      return (document.products ?? []).flatMap(product => {
        if (!product.name || !product.stockCode) return []

        return {
          id: `${document._id}-${product.stockCode}`,
          name: product.name,
          stockCode: product.stockCode.toUpperCase(),
          documentTitle: document.title,
          documentType: document.documentType,
          source: document.source,
          keywords: document.keywords ?? [],
          href,
        }
      })
    })
    .sort((a, b) =>
      a.name.localeCompare(b.name, 'en-GB', {
        numeric: true,
        sensitivity: 'base',
      })
    )

/*
  Lowercases and drops punctuation, keeping . / - and _ for sizes like 12.5mm and 100mm/200mm and
  filenames like DOPerformance_v22. The query and the text it's matched against both go through
  this, so a pasted "Builder's" or "1,000kg" still finds itself.
*/
const normaliseSearchText = (value: string): string =>
  value.toLowerCase().replace(/[^\p{L}\p{N}\s._/-]/gu, '')

/*
  Every word in the query must appear in the product name, document title or type, the entry's
  search keywords, or the stock code. Codes also match in their parent "P" form (PBM001508 finds
  BM001508), since that's the code customers see in product page URLs.
*/
export const filterProductDocuments = (
  rows: ProductDocumentSearchRow[],
  query: string
): ProductDocumentSearchRow[] => {
  const words = normaliseSearchText(query)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, MAX_SEARCH_WORDS)

  if (!words.length) return rows

  return rows.filter(row => {
    const type = DOCUMENT_TYPES[row.documentType]
    const text = normaliseSearchText(
      `${row.name} ${row.documentTitle} ${type?.label} ${type?.name} ${row.keywords.join(' ')}`
    )
    const code = row.stockCode.toLowerCase()

    return words.every(word => text.includes(word) || `p${code}`.includes(word))
  })
}

// Builds the browser row: drops the search-only keywords and adds the image and page link from Brenda
export const addProductDocumentDetails = (
  {
    id,
    name,
    stockCode,
    documentTitle,
    documentType,
    source,
    href,
  }: ProductDocumentSearchRow,
  product?: ProductSearchItem
): ProductDocumentRow => {
  const row = { id, name, stockCode, documentTitle, documentType, source, href }

  // Products not on the site have no image or page to link to
  if (!product?.url?.stockCode || !product.url.slug) {
    return { ...row, image: null, productUrl: null }
  }

  return {
    ...row,
    image: product.image?.imageUrl ?? null,
    productUrl: getProductPageLink({
      productType: product.productType,
      stockCode: product.url.stockCode,
      slug: product.url.slug,
      variant: product.url.option?.value,
    }),
  }
}

// "Showing 1 - 10 out of 33 entries", matching the product listing's wording
export const getProductDocumentsCount = ({
  page,
  pageSize,
  total,
}: {
  page: number
  pageSize: number
  total: number
}): string => {
  const from = (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)

  return `Showing ${from} - ${to} out of ${total} entries`
}
